import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from "prop-types";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

import RDFormatTable from 'components/RDTable/RDFormatTable'
import AdjustmentFootnote from 'components/common/reports/AdjustmentFootnote';
import {
  getAvgCheckHighThreshold,
  applyAvgCheckAdjustment,
  calculateWeightedAvgAdjustment
} from 'services/thresholds/thresholdService';

const columns = require('./r0002Columns')

/**
 * Helper function to calculate weighted average adjustment for aggregated rows
 * @param {Array} stores - Array of store rows
 * @param {Object} thresholds - Threshold configuration
 * @returns {number} Weighted average adjustment in cents
 */
function getAggregatedAdjustment(stores, thresholds) {
  if (!stores || stores.length === 0) {
    return 0;
  }

  // Get adjustment for each store that has a threshold assignment
  const validAdjustments = [];

  stores.forEach(store => {
    // Check if store has a threshold assignment
    const hasThresholdAssignment = store.thresholds?.avgCheck;

    const adjustment = getAvgCheckHighThreshold(
      store.storeId,
      thresholds,
      store.thresholds?.avgCheck // Use row-level threshold assignment
    );

    // Only include stores that have a threshold assignment (even if the adjustment is 0)
    if (hasThresholdAssignment) {
      validAdjustments.push(adjustment);
    }
  });

  // If no stores have threshold assignments, return 0
  if (validAdjustments.length === 0) {
    return 0;
  }

  // Calculate simple average of adjustments (including 0 values for stores with threshold assignments)
  const totalAdjustment = validAdjustments.reduce((sum, adj) => sum + adj, 0);
  const averageAdjustment = totalAdjustment / validAdjustments.length;

  return averageAdjustment;
}

export default function R0002Table(props){
  const [data, setData] = useState([...props.data])
  const [storeIds, setStoreIds] = useState([])

  // const tableColumns = useMemo(() => columns.columns )
  const tableColumns = columns.columns

  useEffect(() => {
    // Extract store IDs for AdjustmentFootnote
    const ids = props.data
      .filter(row => row.rowType === 'storeRow' && row.storeId)
      .map(row => row.storeId);
    setStoreIds(ids);

    let tipData = props.data.map((row) => {
      switch (row.rowType) {
        case 'storeRow': {
          // Get threshold adjustment for this specific store
          // Use row.thresholds.avgCheck which contains the threshold ID
          const highThreshold = getAvgCheckHighThreshold(
            row.storeId,
            props.thresholds,
            row.thresholds?.avgCheck || (props.storeThresholds || {})
          );

          if (props.storeTypeSwitch && props.compStoreSwitch) {
            // Store Type is Franchise & comparing to Company stores
            return {
              ...row,
              tipType: 'company',
              tipNSTotal: row.compNSTotal,
              tipNSDineIn: row.compNSDineIn,
              tipNSToGo: row.compNSToGo,
              tipNSDelivery: row.compNSDelivery,
              tipGSDelivery: row.compGSDelivery,
              tipCCTotal: row.compCCTotal,
              tipCCDineIn: row.compCCDineIn,
              tipCCToGo: row.compCCToGo,
              tipCCDelivery: row.compCCDelivery,
              // compAC* values are already adjusted in buildR0002Pages, use directly
              tipACTotal: row.compACTotal,
              tipACDineIn: row.compACDineIn,
              tipACToGo: row.compACToGo,
              tipACDelivery: row.compACDelivery,
              tipSPLHTotal: row.compSPLHTotal,
              tipFOH: row.compFOH,
              tipBOH: row.compBOH,
              tipOTTotal: row.compOTTotal,
              tipOTFOH: row.compOTFOH,
              tipOTBOH: row.compOTBOH,
              tipLaborPct: row.compLaborPct,
            };
          } else {
            return {
              ...row,
              tipType: 'previous',
              tipNSTotal: row.pNSTotal,
              tipNSDineIn: row.pNSDineIn,
              tipNSToGo: row.pNSToGo,
              tipNSDelivery: row.pNSDelivery,
              tipGSDelivery: row.pGSDelivery,
              tipCCTotal: row.pCCTotal,
              tipCCDineIn: row.pCCDineIn,
              tipCCToGo: row.pCCToGo,
              tipCCDelivery: row.pCCDelivery,
              // pAC* values are already adjusted in buildR0002Pages, use directly
              tipACTotal: row.pACTotal,
              tipACDineIn: row.pACDineIn,
              tipACToGo: row.pACToGo,
              tipACDelivery: row.pACDelivery,
              tipSPLHTotal: row.pSPLHTotal,
              tipFOH: row.pFOH,
              tipBOH: row.pBOH,
              tipOTTotal: row.pOTTotal,
              tipOTFOH: row.pOTFOH,
              tipOTBOH: row.pOTBOH,
              tipLaborPct: row.pLaborPct,
            };
          }
        }
        case 'areaRow':
        case 'regionRow':
        case 'totalRow': {
          // Calculate weighted average adjustment for aggregated rows
          // Determine which stores to include based on row type and name
          let stores = [];

          if (row.store === 'Total Comp' || row.store === 'Overall Total') {
            // Include all established stores (storeRow type)
            stores = props.data.filter(r => r.rowType === 'storeRow');
          } else if (row.store && row.store.endsWith(' Total')) {
            // Area/Manager total row - extract manager name and filter by manager
            const managerName = row.store.replace(' Total', '');
            stores = props.data.filter(r => {
              if (r.rowType !== 'storeRow') return false;
              return r.manager === managerName;
            });
          } else {
            // Fallback for other aggregated rows
            stores = props.data.filter(r => r.rowType === 'storeRow');
          }

          if (props.storeTypeSwitch && props.compStoreSwitch) {
            return {
              ...row,
              tipType: 'company',
              tipNSTotal: row.compNSTotal,
              tipNSDineIn: row.compNSDineIn,
              tipNSToGo: row.compNSToGo,
              tipNSDelivery: row.compNSDelivery,
              tipGSDelivery: row.compGSDelivery,
              tipCCTotal: row.compCCTotal,
              tipCCDineIn: row.compCCDineIn,
              tipCCToGo: row.compCCToGo,
              tipCCDelivery: row.compCCDelivery,
              // compAC* values are already adjusted in buildR0002Pages, use directly
              tipACTotal: row.compACTotal,
              tipACDineIn: row.compACDineIn,
              tipACToGo: row.compACToGo,
              tipACDelivery: row.compACDelivery,
              tipSPLHTotal: row.compSPLHTotal,
              tipFOH: row.compFOH,
              tipBOH: row.compBOH,
              tipOTTotal: row.compOTTotal,
              tipOTFOH: row.compOTFOH,
              tipOTBOH: row.compOTBOH,
              tipLaborPct: row.compLaborPct,
            };
          } else {
            return {
              ...row,
              tipType: 'previous',
              tipNSTotal: row.pNSTotal,
              tipNSDineIn: row.pNSDineIn,
              tipNSToGo: row.pNSToGo,
              tipNSDelivery: row.pNSDelivery,
              tipGSDelivery: row.pGSDelivery,
              tipCCTotal: row.pCCTotal,
              tipCCDineIn: row.pCCDineIn,
              tipCCToGo: row.pCCToGo,
              tipCCDelivery: row.pCCDelivery,
              // pAC* values are already adjusted in buildR0002Pages, use directly
              tipACTotal: row.pACTotal,
              tipACDineIn: row.pACDineIn,
              tipACToGo: row.pACToGo,
              tipACDelivery: row.pACDelivery,
              tipSPLHTotal: row.pSPLHTotal,
              tipFOH: row.pFOH,
              tipBOH: row.pBOH,
              tipOTTotal: row.pOTTotal,
              tipOTFOH: row.pOTFOH,
              tipOTBOH: row.pOTBOH,
              tipLaborPct: row.pLaborPct,
            };
          }
        }
        default:
          return row;
      }
    });

    setData(tipData);
  }, [props.data, props.storeTypeSwitch, props.compStoreSwitch, props.thresholds, props.storeThresholds]);
  // const data = useMemo(() => tipData, [])

  return (
    <MDBox
      sx={{backgroundColor: '#e0e0e0'}}
    >
      <RDFormatTable
        columns={tableColumns}
        data={data}
        conditions={props.thresholds}
        compStore={props.compStoreSwitch}
      />
      <AdjustmentFootnote
        storeIds={storeIds}
        thresholds={props.thresholds}
        storeThresholds={props.storeThresholds || {}}
      />
    </MDBox>
  )
}

R0002Table.propTypes = {
  compStoreSwitch: PropTypes.bool,
  data: PropTypes.array.isRequired,
  storeTypeSwitch: PropTypes.bool,
  thresholds: PropTypes.array,
  storeThresholds: PropTypes.object
};